import { Technology } from '@common/technology.enum';
import { LibraryStatus } from '../base/library-status.enum';
import { LibrarySpecimen } from '../base/library-specimen.model';
import { PolymorphicLibrary } from '../library-types.model';
import { LibraryNGS } from '../technologies/ngs/library-ngs.model';
import { LibraryMicroarray } from '../technologies/microarray/library-microarray.model';
import { LibraryMLPA } from '../technologies/shared-mlpa-triplet/library-mlpa.model';
import { LibraryTripletExpansion } from '../technologies/shared-mlpa-triplet/library-triplet-expansion.model';
import { createBatchInfo } from '../shared/batch-info.model';
import {
  RunLibraryAssignment,
  isMicroarrayLibraryAssignment,
  isMlpaLibraryAssignment,
  isTripletExpansionLibraryAssignment
} from '../../sequencing/sequencing-run-detail.model';
import {
  CreateLibraryCommand,
  isCreateLibraryNGSCommand,
  isCreateLibraryMicroarrayCommand,
  isCreateLibraryMLPACommand,
  isCreateLibraryTripletExpansionCommand
} from '../commands/library-create-command';

interface LibraryBackendBaseProps {
  readonly libraryId: string;
  readonly libraryCode: string;
  readonly status: LibraryStatus;
  readonly specimens: LibrarySpecimen[];
  readonly sampleCount: number;
  readonly createdByUserId: string | null;
  readonly updatedByUserId: string | null;
  readonly createdAt: Date;
  readonly updatedAt: Date;
}

export interface LibrarySpecimenBackendDto {
  libSpecimenId: string;
  specimenId: string;
  indexAdapterId: string | null;
  indexValue: string | null;
  lgCode: string | null;
  position: string | null;
  assignmentDate: string | null;
}

// ============================================
// Discriminated Union DTOs by Technology
// ============================================

interface LibraryBackendDtoBase {
  readonly libraryId: string;
  readonly libraryCode: string;
  readonly status: string;
  readonly sampleCount: number;
  readonly specimens: LibrarySpecimenBackendDto[];
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly createdByUserId: string | null;
  readonly updatedByUserId: string | null;
}

export interface LibraryNGSBackendDto extends LibraryBackendDtoBase {
  readonly technology: 'NGS';
  readonly cellId?: string;
  readonly plateId?: string | null;
  readonly indexId?: string | null;
}

export interface LibraryMicroarrayBackendDto extends LibraryBackendDtoBase {
  readonly technology: 'MICROARREGLOS';
  readonly chipType?: string;
  readonly arrangementType?: string;
  readonly plateName?: string | null;
  readonly chipLot?: string | null;
  readonly chipReference?: string | null;
}

export interface BatchInfoBackendDto {
  readonly lot?: string | null;
  readonly reference?: string | null;
  readonly position?: string | null;
}

export interface LibraryMLPABackendDto extends LibraryBackendDtoBase {
  readonly technology: 'MLPA';
  readonly equipment?: string | null;
  readonly creationDate?: string | null;
  readonly plateName?: string | null;
  readonly batchInfo?: BatchInfoBackendDto | null;
}

export interface LibraryTripletBackendDto extends LibraryBackendDtoBase {
  readonly technology: 'EXPANSION_TRIPLETAS';
  readonly equipment?: string | null;
  readonly creationDate?: string | null;
  readonly plateName?: string | null;
  readonly batchInfo?: BatchInfoBackendDto | null;
}

export type LibraryBackendDto =
  | LibraryNGSBackendDto
  | LibraryMicroarrayBackendDto
  | LibraryMLPABackendDto
  | LibraryTripletBackendDto;

// Type guards
export function isNGSBackendDto(
  dto: LibraryBackendDto
): dto is LibraryNGSBackendDto {
  return dto.technology === 'NGS';
}

export function isMicroarrayBackendDto(
  dto: LibraryBackendDto
): dto is LibraryMicroarrayBackendDto {
  return dto.technology === 'MICROARREGLOS';
}

export function isMLPABackendDto(
  dto: LibraryBackendDto
): dto is LibraryMLPABackendDto {
  return dto.technology === 'MLPA';
}

export function isTripletBackendDto(
  dto: LibraryBackendDto
): dto is LibraryTripletBackendDto {
  return dto.technology === 'EXPANSION_TRIPLETAS';
}

export class LibraryBackendMapper {
  private constructor() {
    throw new Error(
      'LibraryBackendMapper is a utility class and cannot be instantiated'
    );
  }

  static fromBackendDto(dto: LibraryBackendDto): PolymorphicLibrary {
    const baseProps = LibraryBackendMapper.mapBaseProps(dto);

    switch (dto.technology) {
      case 'NGS':
        return LibraryBackendMapper.mapToNGS(dto, baseProps);

      case 'MICROARREGLOS':
        return LibraryBackendMapper.mapToMicroarray(dto, baseProps);

      case 'MLPA':
        return LibraryBackendMapper.mapToMLPA(dto, baseProps);

      case 'EXPANSION_TRIPLETAS':
        return LibraryBackendMapper.mapToTripletExpansion(dto, baseProps);

      default: {
        const _exhaustiveCheck: never = dto;
        throw new Error(
          `Unknown technology: ${(_exhaustiveCheck as LibraryBackendDto).technology}`
        );
      }
    }
  }

  static fromBackendDtoList(dtos: LibraryBackendDto[]): PolymorphicLibrary[] {
    return dtos.map((dto) => LibraryBackendMapper.fromBackendDto(dto));
  }

  static fromRunLibraryAssignment(
    assignment: RunLibraryAssignment,
    technology: Technology
  ): PolymorphicLibrary {
    const specimens: LibrarySpecimen[] = assignment.specimens.map((s) => ({
      libSpecimenId: s.libSpecimenId,
      specimenId: s.specimenId,
      lgCode: s.lgCode,
      indexAdapterId: s.indexAdapterId,
      indexValue: s.indexValue,
      position: null,
      assignmentDate: new Date(s.assignmentDate)
    }));

    const baseProps: LibraryBackendBaseProps = {
      libraryId: assignment.libraryId,
      libraryCode: assignment.libraryCode,
      status: LibraryStatus.IN_RUN,
      specimens,
      sampleCount: specimens.length,
      createdByUserId: null,
      updatedByUserId: null,
      createdAt: new Date(assignment.createdAt),
      updatedAt: new Date(assignment.createdAt)
    };

    switch (technology) {
      case Technology.NGS:
        return LibraryBackendMapper.mapRunToNGS(baseProps);

      case Technology.MICROARREGLOS:
        return LibraryBackendMapper.mapRunToMicroarray(assignment, baseProps);

      case Technology.MLPA:
        return LibraryBackendMapper.mapRunToMLPA(assignment, baseProps);

      case Technology.EXPANSION_TRIPLETAS:
        return LibraryBackendMapper.mapRunToTripletExpansion(
          assignment,
          baseProps
        );

      default:
        throw new Error(`Unknown technology: ${technology}`);
    }
  }

  static fromRunLibraryAssignmentList(
    assignments: RunLibraryAssignment[],
    technology: Technology
  ): Map<string, PolymorphicLibrary> {
    const map = new Map<string, PolymorphicLibrary>();
    assignments.forEach((assignment) => {
      if (!map.has(assignment.libraryId)) {
        map.set(
          assignment.libraryId,
          LibraryBackendMapper.fromRunLibraryAssignment(assignment, technology)
        );
      }
    });
    return map;
  }

  private static mapRunToNGS(
    baseProps: LibraryBackendBaseProps
  ): LibraryNGS {
    return {
      ...baseProps,
      technology: Technology.NGS,
      cellId: '',
      plateId: null,
      indexId: null
    };
  }

  private static mapRunToMicroarray(
    assignment: RunLibraryAssignment,
    baseProps: LibraryBackendBaseProps
  ): LibraryMicroarray {
    if (isMicroarrayLibraryAssignment(assignment)) {
      return {
        ...baseProps,
        technology: Technology.MICROARREGLOS,
        chipType: assignment.chipType ?? '',
        arrangementType: assignment.arrangementType ?? '',
        plateName: assignment.plateName ?? null,
        batchInfo: createBatchInfo(
          assignment.chipLot,
          assignment.chipReference
        )
      };
    }
    return {
      ...baseProps,
      technology: Technology.MICROARREGLOS,
      chipType: '',
      arrangementType: '',
      plateName: null,
      batchInfo: createBatchInfo(null, null)
    };
  }

  private static mapRunToMLPA(
    assignment: RunLibraryAssignment,
    baseProps: LibraryBackendBaseProps
  ): LibraryMLPA {
    if (isMlpaLibraryAssignment(assignment)) {
      return {
        ...baseProps,
        technology: Technology.MLPA,
        creationDate: assignment.creationDate
          ? new Date(assignment.creationDate)
          : null,
        plateName: assignment.plateName ?? null,
        equipment: assignment.equipment ?? null,
        batchInfo: createBatchInfo(
          assignment.batchLot,
          assignment.batchReference
        )
      };
    }
    return {
      ...baseProps,
      technology: Technology.MLPA,
      creationDate: null,
      plateName: null,
      equipment: null,
      batchInfo: createBatchInfo(null, null)
    };
  }

  private static mapRunToTripletExpansion(
    assignment: RunLibraryAssignment,
    baseProps: LibraryBackendBaseProps
  ): LibraryTripletExpansion {
    if (isTripletExpansionLibraryAssignment(assignment)) {
      return {
        ...baseProps,
        technology: Technology.EXPANSION_TRIPLETAS,
        creationDate: assignment.creationDate
          ? new Date(assignment.creationDate)
          : null,
        plateName: assignment.plateName ?? null,
        equipment: assignment.equipment ?? null,
        batchInfo: createBatchInfo(
          assignment.batchLot,
          assignment.batchReference
        )
      };
    }
    return {
      ...baseProps,
      technology: Technology.EXPANSION_TRIPLETAS,
      creationDate: null,
      plateName: null,
      equipment: null,
      batchInfo: createBatchInfo(null, null)
    };
  }

  private static mapBaseProps(dto: LibraryBackendDto): LibraryBackendBaseProps {
    const specimens: LibrarySpecimen[] = dto.specimens.map((s) => ({
      libSpecimenId: s.libSpecimenId,
      specimenId: s.specimenId,
      lgCode: s.lgCode,
      indexAdapterId: s.indexAdapterId,
      indexValue: s.indexValue,
      position: s.position,
      assignmentDate: s.assignmentDate ? new Date(s.assignmentDate) : null
    }));

    return {
      libraryId: dto.libraryId,
      libraryCode: dto.libraryCode,
      status: LibraryBackendMapper.parseStatus(dto.status),
      specimens,
      sampleCount: dto.sampleCount,
      createdByUserId: dto.createdByUserId,
      updatedByUserId: dto.updatedByUserId,
      createdAt: new Date(dto.createdAt),
      updatedAt: new Date(dto.updatedAt)
    };
  }

  private static mapToNGS(
    dto: LibraryNGSBackendDto,
    baseProps: LibraryBackendBaseProps
  ): LibraryNGS {
    return {
      ...baseProps,
      technology: Technology.NGS,
      cellId: dto.cellId ?? '',
      plateId: dto.plateId ?? null,
      indexId: dto.indexId ?? null
    };
  }

  private static mapToMicroarray(
    dto: LibraryMicroarrayBackendDto,
    baseProps: LibraryBackendBaseProps
  ): LibraryMicroarray {
    return {
      ...baseProps,
      technology: Technology.MICROARREGLOS,
      chipType: dto.chipType ?? '',
      arrangementType: dto.arrangementType ?? '',
      plateName: dto.plateName ?? null,
      batchInfo: createBatchInfo(dto.chipLot, dto.chipReference)
    };
  }

  private static mapToMLPA(
    dto: LibraryMLPABackendDto,
    baseProps: LibraryBackendBaseProps
  ): LibraryMLPA {
    return {
      ...baseProps,
      technology: Technology.MLPA,
      creationDate: dto.creationDate ? new Date(dto.creationDate) : null,
      plateName: dto.plateName ?? null,
      equipment: dto.equipment ?? null,
      batchInfo: createBatchInfo(
        dto.batchInfo?.lot,
        dto.batchInfo?.reference,
        dto.batchInfo?.position
      )
    };
  }

  private static mapToTripletExpansion(
    dto: LibraryTripletBackendDto,
    baseProps: LibraryBackendBaseProps
  ): LibraryTripletExpansion {
    return {
      ...baseProps,
      technology: Technology.EXPANSION_TRIPLETAS,
      creationDate: dto.creationDate ? new Date(dto.creationDate) : null,
      plateName: dto.plateName ?? null,
      equipment: dto.equipment ?? null,
      batchInfo: createBatchInfo(
        dto.batchInfo?.lot,
        dto.batchInfo?.reference,
        dto.batchInfo?.position
      )
    };
  }

  private static parseStatus(status: string): LibraryStatus {
    const normalized = status.toUpperCase();
    switch (normalized) {
      case 'CREATED':
        return LibraryStatus.CREATED;
      case 'IN_RUN':
        return LibraryStatus.IN_RUN;
      case 'COMPLETED':
        return LibraryStatus.COMPLETED;
      case 'DISABLED':
        return LibraryStatus.DISABLED;
      default:
        return LibraryStatus.CREATED;
    }
  }

  static toBackendRequest(
    command: CreateLibraryCommand
  ): CreateLibraryBackendRequest {
    const baseSamples = command.specimens.map((s) => ({
      lgCode: s.lgCode,
      indexAdapterId: s.indexAdapterId ?? null,
      position: s.position ?? null
    }));

    if (isCreateLibraryNGSCommand(command)) {
      const group: CreateLibraryGroupNGSBackendRequest = {
        libraryCode: command.libraryCode,
        samples: baseSamples,
        plateId: command.plateId,
        indexId: command.indexId
      };
      return {
        technology: command.technology,
        cellId: command.cellId,
        libraryGroups: [group]
      };
    }

    if (isCreateLibraryMicroarrayCommand(command)) {
      const group: CreateLibraryGroupMicroarrayBackendRequest = {
        libraryCode: command.libraryCode,
        samples: baseSamples,
        chipType: command.chipType,
        arrangementType: command.arrangementType,
        plateName: command.plateName,
        chipLot: command.batchInfo?.lot,
        chipReference: command.batchInfo?.reference
      };
      return {
        technology: command.technology,
        libraryGroups: [group]
      };
    }

    if (isCreateLibraryMLPACommand(command)) {
      const group: CreateLibraryGroupMLPABackendRequest = {
        libraryCode: command.libraryCode,
        samples: baseSamples,
        creationDate: command.creationDate?.toISOString(),
        plateName: command.plateName,
        equipment: command.equipment,
        lot: command.batchInfo?.lot,
        reference: command.batchInfo?.reference
      };
      return {
        technology: command.technology,
        libraryGroups: [group]
      };
    }

    if (isCreateLibraryTripletExpansionCommand(command)) {
      const group: CreateLibraryGroupTripletBackendRequest = {
        libraryCode: command.libraryCode,
        samples: baseSamples,
        creationDate: command.creationDate?.toISOString(),
        plateName: command.plateName,
        equipment: command.equipment,
        lot: command.batchInfo?.lot,
        reference: command.batchInfo?.reference
      };
      return {
        technology: command.technology,
        libraryGroups: [group]
      };
    }

    // Exhaustive check
    const _exhaustiveCheck: never = command;
    throw new Error(
      `Unknown command type: ${(_exhaustiveCheck as CreateLibraryCommand).technology}`
    );
  }
}

export interface CreateLibraryBackendRequest {
  technology: string;
  cellId?: string;
  libraryGroups: CreateLibraryGroupBackendRequest[];
}

interface CreateLibraryGroupBackendRequestBase {
  libraryCode?: string;
  samples: CreateLibrarySampleBackendRequest[];
}

export interface CreateLibraryGroupNGSBackendRequest
  extends CreateLibraryGroupBackendRequestBase {
  plateId?: string;
  indexId?: string;
}

export interface CreateLibraryGroupMicroarrayBackendRequest
  extends CreateLibraryGroupBackendRequestBase {
  chipType: string;
  arrangementType: string;
  plateName?: string | null;
  chipLot?: string | null;
  chipReference?: string | null;
}

export interface CreateLibraryGroupMLPABackendRequest
  extends CreateLibraryGroupBackendRequestBase {
  creationDate?: string;
  plateName?: string | null;
  equipment?: string | null;
  lot?: string | null;
  reference?: string | null;
}

export interface CreateLibraryGroupTripletBackendRequest
  extends CreateLibraryGroupBackendRequestBase {
  creationDate?: string;
  plateName?: string | null;
  equipment?: string | null;
  lot?: string | null;
  reference?: string | null;
}

export type CreateLibraryGroupBackendRequest =
  | CreateLibraryGroupNGSBackendRequest
  | CreateLibraryGroupMicroarrayBackendRequest
  | CreateLibraryGroupMLPABackendRequest
  | CreateLibraryGroupTripletBackendRequest;

export interface CreateLibrarySampleBackendRequest {
  lgCode: string;
  indexAdapterId?: string | null;
  position?: string | null;
}
